import { BufferAttribute, BufferGeometry, Line3, Mesh, Vector3 } from "three";
import { GeometryUtils, StyleUtils, VertexSemantic } from "../../../utils/internal";
import { GeometryNode } from "./internal";
/**
 * Creates a point geometry
 *
 * @class PointNode
 * @extends {SceneGraphNode}
 * @constructor
 */
export class PolygonNode extends GeometryNode {
    /**
     * @method constructor
     * @param {number[]} coordinates position of box in lv95 [m]
     * @param {number} size - the sidelength in [m]
     */
    constructor(coordinates, material, parent) {
        super(null);
        this.parent = parent;
        this.coordinates = coordinates.slice();
        if (coordinates[0].length == 2) {
            this.is2dfeature = true;
            for (let i = 0; i < coordinates.length; i++) {
                this.coordinates[i] = coordinates[i].slice(0);
                this.coordinates[i][2] = 0.0;
            }
        }
        this._centroid = [0, 0, 0];
        this.vertexSemantic = new VertexSemantic("v3");
        this.material = StyleUtils.CreatePolygonMaterial(material.polygon, this.parent.scene.engine);
    }
    init(traversalState) {
        super.init(traversalState);
        this.needsupdate = true;
        this.rebuildBufferUnit();
    }
    render(traversalState) {
        super.render(traversalState);
        if (this.object3D != null)
            if (!this.object3D.visible)
                return;
        if (this.is2dfeature) {
            //calc the height if no height is given
            if (traversalState.groundPlaneUpdated || this.needsupdate) {
                //update the z values
                for (let i = 0; i < this.coordinates.length; i++) {
                    const p1 = new Vector3(this.coordinates[i][0], this.coordinates[i][1], -8000);
                    const p2 = new Vector3(this.coordinates[i][0], this.coordinates[i][1], 8000);
                    const l1 = new Line3(p1, p2);
                    const target = new Vector3();
                    const pi = traversalState.groundPlane.intersectLine(l1, target);
                    if (pi != null)
                        this.coordinates[i][2] = pi.z;
                }
                this.needsupdate = true;
            }
        }
        if (this._centroid != traversalState.scene.engine.centroid || this.needsupdate) {
            this.needsupdate = false;
            this._centroid = traversalState.scene.engine.centroid;
            this.rebuildBufferUnit();
        }
    }
    show() {
        this.object3D.visible = true;
        this.needsupdate = true;
    }
    hide() {
        this.object3D.visible = false;
        this.needsupdate = true;
    }
    setStyle(material) {
        this.material = StyleUtils.CreatePolygonMaterial(material.polygon, this.parent.scene.engine);
        this.iMesh.material = this.material;
        this.iMesh.material.needsUpdate = true;
        this.needsupdate = true;
    }
    pick(mx, my) {
        return this.currentTraversalState.rayCaster.intersectObject(this.iMesh);
    }
    updateCoordinates(coordinates) {
        this.coordinates = coordinates.slice();
        if (this.is2dfeature) {
            for (let i = 0; i < coordinates.length; i++) {
                this.coordinates[i] = coordinates[i].slice(0);
                this.coordinates[i][2] = 0.0;
            }
        }
        this.needsupdate = true;
        this.rebuildBufferUnit();
    }
    rebuildBufferUnit() {
        const positions = [];
        const colors = [];
        const indices = [];
        const normals = [];
        let vertexIndex = 0;
        // interate vertices
        if (this.object3D != null) {
            const obj = this.object3D;
            if (this.coordinates.length < 3)
                return;
            let currentNormal = new Vector3();
            const vertices = GeometryUtils.Triangulate(this.coordinates, this.vertexSemantic);
            if (obj.visible && vertices.length > 0) {
                for (let h = 0; h < vertices.length; h++) {
                    // Check if the vertices have colors
                    positions.push(vertices[h][this.vertexSemantic.vertices[0]] - this._centroid[0] || 0, vertices[h][this.vertexSemantic.vertices[1]] - this._centroid[1] || 0, vertices[h][this.vertexSemantic.vertices[2]] - this._centroid[2] || 0);
                    // triangles expected so push every 3 vertices to index and create a normal
                    if (this.vertexSemantic.hasIndex) {
                        indices.push(vertices[h][this.vertexSemantic.index]);
                    }
                    else {
                        indices.push(vertexIndex + h);
                    }
                    if (this.vertexSemantic.hasNormal) {
                        normals.push(vertices[h][this.vertexSemantic.normals[0]], vertices[h][this.vertexSemantic.normals[1]], vertices[h][this.vertexSemantic.normals[2]]);
                    }
                    else {
                        if (vertices.length > 0 && vertices.length % 3 == 0 && h % 3 == 0) {
                            const pA = new Vector3().set(vertices[h][this.vertexSemantic.vertices[0]], vertices[h][this.vertexSemantic.vertices[1]], vertices[h][this.vertexSemantic.vertices[2]]);
                            const pB = new Vector3().set(vertices[h + 1][this.vertexSemantic.vertices[0]], vertices[h + 1][this.vertexSemantic.vertices[1]], vertices[h + 1][this.vertexSemantic.vertices[2]]);
                            const pC = new Vector3().set(vertices[h + 2][this.vertexSemantic.vertices[0]], vertices[h + 2][this.vertexSemantic.vertices[1]], vertices[h + 2][this.vertexSemantic.vertices[2]]);
                            const cb = new Vector3().subVectors(pC, pB);
                            const ab = new Vector3().subVectors(pA, pB);
                            cb.cross(ab);
                            // normal
                            cb.normalize();
                            currentNormal = cb;
                        }
                        normals.push(currentNormal.x, currentNormal.y, currentNormal.z);
                    }
                    if (this.vertexSemantic.hasColor) {
                        colors.push(vertices[h][this.vertexSemantic.colors[0]], vertices[h][this.vertexSemantic.colors[1]], vertices[h][this.vertexSemantic.colors[2]]);
                    }
                }
                vertexIndex += vertices.length;
            }
        }
        // create first
        if (this.object3D == null) {
            this.geometry = new BufferGeometry();
            // commong attirbutes
            this.geometry.setIndex(new BufferAttribute(new Uint16Array(indices), 1));
            this.geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
            this.geometry.setAttribute("normal", new BufferAttribute(new Float32Array(normals), 3));
            if (this.vertexSemantic.hasColor) {
                // vertex colors
                this.geometry.setAttribute("color", new BufferAttribute(new Float32Array(colors), 3));
            }
            this.geometry.computeBoundingSphere();
            this.iMesh = new Mesh(this.geometry, this.material);
            this.iMesh.visible = this.parent.isVisible();
            this.object3D = this.iMesh;
        }
        else {
            // use exisitng
            this.geometry.setIndex(new BufferAttribute(new Uint32Array(indices), 1));
            this.geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
            this.geometry.setAttribute("normal", new BufferAttribute(new Float32Array(normals), 3));
            if (this.vertexSemantic.hasColor) {
                this.geometry.setAttribute("color", new BufferAttribute(new Float32Array(colors), 3));
            }
            try {
                this.geometry.computeBoundingSphere();
            }
            catch (e) {
                console.log("could not compute bounding sphere of polygon...");
            }
        }
    }
    destroy() {
        this.coordinates = null;
        this._centroid = null;
        this.geometry = null;
        this.object3D = null;
        this.iMesh = null;
        this.vertexSemantic = null;
        this.parent = null;
        this.lastpickinfo = null;
        this.vertices = null;
    }
}
//# sourceMappingURL=polygonnode.js.map